import { useEffect } from "react";
import { PUBLIC_PATHS, SITE_ORIGIN } from "@cafonapay/shared/client";
import type { Locale, MessageKey } from "./i18n";

/**
 * Search-engine metadata for every route: title, description, canonical URL, language
 * alternates (hreflang), Open Graph / Twitter cards and robots.
 *
 * Only public marketing and information pages are indexable. Everything behind sign-in, every
 * payment or invitation link, and every personal page is `noindex` (and disallowed in robots.txt).
 * Canonical URLs always point at production, so staging and previews never compete with it.
 */
export const SITE = SITE_ORIGIN;

export interface PublicPage {
  /** Path without a language prefix ("/", "/legal/terms"). */
  path: string;
  title: MessageKey;
  description: MessageKey;
  /** Has a French version under /fr. */
  bilingual: boolean;
  priority: number;
}

const COPY: Record<string, { title: MessageKey; description: MessageKey }> = {
  "/": { title: "seo.home.title", description: "seo.home.description" },
  "/legal/terms": { title: "seo.terms.title", description: "seo.terms.description" },
  "/legal/privacy": { title: "seo.privacy.title", description: "seo.privacy.description" },
  "/developers": { title: "seo.dev.title", description: "seo.dev.description" },
  "/verify": { title: "seo.verify.title", description: "seo.verify.description" },
};

/** The indexable pages (the list is shared with the Worker's sitemap.xml). */
export const PUBLIC_PAGES: PublicPage[] = PUBLIC_PATHS.map((p) => ({ ...p, ...COPY[p.path]! }));

/** "/fr/legal/terms" → { base: "/legal/terms", lang: "fr" }. */
export function splitLang(pathname: string): { base: string; lang: Locale | null } {
  const clean = pathname.replace(/\/+$/, "") || "/";
  if (clean === "/fr") return { base: "/", lang: "fr" };
  if (clean.startsWith("/fr/")) return { base: clean.slice(3), lang: "fr" };
  return { base: clean, lang: null };
}
export const localized = (base: string, lang: Locale) => (lang === "fr" ? (base === "/" ? "/fr" : `/fr${base}`) : base);

function setMeta(attr: "name" | "property", key: string, content: string | null) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (content === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setLinks(rel: string, items: Array<{ href: string; hreflang?: string }>) {
  document.head.querySelectorAll(`link[rel="${rel}"][data-seo]`).forEach((l) => l.remove());
  for (const i of items) {
    const l = document.createElement("link");
    l.rel = rel;
    l.href = i.href;
    if (i.hreflang) l.hreflang = i.hreflang;
    l.setAttribute("data-seo", "");
    document.head.appendChild(l);
  }
}

export interface SeoHead {
  indexable: boolean;
  lang: Locale;
  title: string;
  description: string;
  url: string | null;
  alternates: Array<{ href: string; hreflang: string }>;
  meta: Array<{ attr: "name" | "property"; key: string; content: string }>;
}

/** The head of an indexable public page, or null for private / non-indexed routes. Pure: used by the app and by the build-time pre-render. */
export function seoHead(pathname: string, t: (k: MessageKey) => string): SeoHead | null {
  const { base, lang } = splitLang(pathname);
  const page = PUBLIC_PAGES.find((p) => p.path === base);
  if (!page || (lang === "fr" && !page.bilingual)) return null;
  // The URL decides the language of an indexable page: /fr/… is French, the rest English.
  const pageLang: Locale = lang ?? "en";
  const url = `${SITE}${localized(base, pageLang)}`;
  const title = t(page.title);
  const description = t(page.description);
  const image = `${SITE}/og-image${pageLang === "fr" ? "-fr" : ""}.png`;
  const meta: SeoHead["meta"] = [
    { attr: "name", key: "description", content: description },
    { attr: "name", key: "robots", content: "index, follow, max-image-preview:large" },
    { attr: "property", key: "og:type", content: "website" },
    { attr: "property", key: "og:site_name", content: "CafonaPay" },
    { attr: "property", key: "og:title", content: title },
    { attr: "property", key: "og:description", content: description },
    { attr: "property", key: "og:url", content: url },
    { attr: "property", key: "og:locale", content: pageLang === "fr" ? "fr_CA" : "en_CA" },
    ...(page.bilingual ? [{ attr: "property" as const, key: "og:locale:alternate", content: pageLang === "fr" ? "en_CA" : "fr_CA" }] : []),
    { attr: "property", key: "og:image", content: image },
    { attr: "property", key: "og:image:width", content: "1200" },
    { attr: "property", key: "og:image:height", content: "630" },
    { attr: "property", key: "og:image:alt", content: title },
    { attr: "name", key: "twitter:card", content: "summary_large_image" },
    { attr: "name", key: "twitter:title", content: title },
    { attr: "name", key: "twitter:description", content: description },
    { attr: "name", key: "twitter:image", content: image },
  ];
  return {
    indexable: true,
    lang: pageLang,
    title,
    description,
    url,
    alternates: page.bilingual
      ? [
          { href: `${SITE}${localized(base, "en")}`, hreflang: "en" },
          { href: `${SITE}${localized(base, "fr")}`, hreflang: "fr" },
          { href: `${SITE}${localized(base, "en")}`, hreflang: "x-default" },
        ]
      : [],
    meta,
  };
}

const esc = (v: string) => v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/** The same head as HTML, for pre-rendered files. */
export function seoHeadHtml(h: SeoHead): string {
  return [
    `<title>${esc(h.title)}</title>`,
    `<link rel="canonical" href="${h.url}" data-seo />`,
    ...h.alternates.map((a) => `<link rel="alternate" hreflang="${a.hreflang}" href="${a.href}" data-seo />`),
    ...h.meta.map((m) => `<meta ${m.attr}="${m.key}" content="${esc(m.content)}" />`),
  ].join("\n    ");
}

/** Applies the head tags for the current route. Private routes get their title and noindex only. */
export function useSeo(pathname: string, locale: Locale, t: (k: MessageKey) => string, privateTitle: string | null) {
  useEffect(() => {
    const h = seoHead(pathname, t);
    if (!h) {
      document.title = privateTitle ? `${privateTitle} · CafonaPay` : `CafonaPay · ${t("landing.title")}`;
      setMeta("name", "robots", "noindex, nofollow");
      setLinks("canonical", []);
      setLinks("alternate", []);
      return;
    }
    document.title = h.title;
    document.documentElement.lang = h.lang === "fr" ? "fr-CA" : "en-CA";
    setLinks("canonical", [{ href: h.url! }]);
    setLinks("alternate", h.alternates);
    for (const m of h.meta) setMeta(m.attr, m.key, m.content);
    if (!h.meta.some((m) => m.key === "og:locale:alternate")) setMeta("property", "og:locale:alternate", null);
  }, [pathname, locale, t, privateTitle]);
}
